import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';
import type { JwtPayload } from '../../types';
import { getToken, createUser } from '../../services/api';
import { getStoredToken, setStoredToken, removeStoredToken, removeStoredAgentKey, decodeToken } from '../../utils/auth';

interface AuthState {
  token: string | null;
  userName: string | null;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  statusText: string | null;
  registerStatusText: string | null;
  isConnected: boolean;
}

// Seed auth optimistically from localStorage so a page refresh doesn't
// kick the user back to `/` via ProtectedRoute → /login → /. The token
// is still validated against the server on boot (App.tsx) and cleared
// on definitive rejection, but in the meantime ProtectedRoute lets the
// user stay on their current route.
function bootstrapFromStorage(): Pick<AuthState, 'token' | 'userName' | 'isAuthenticated'> {
  const token = getStoredToken();
  if (!token) return { token: null, userName: null, isAuthenticated: false };
  try {
    const payload = jwtDecode<JwtPayload>(token);
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp <= now) {
      removeStoredToken();
      return { token: null, userName: null, isAuthenticated: false };
    }
    return { token, userName: payload.email ?? null, isAuthenticated: true };
  } catch {
    removeStoredToken();
    return { token: null, userName: null, isAuthenticated: false };
  }
}

const initialState: AuthState = {
  ...bootstrapFromStorage(),
  isAuthenticating: false,
  statusText: null,
  registerStatusText: null,
  isConnected: false,
};

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }: { email: string; password: string }) => {
    const response = await getToken(email, password);
    return response.data;
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ email, password }: { email: string; password: string }) => {
    const response = await createUser(email, password);
    return response.data;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginUserSuccess(state, action) {
      const token = action.payload.token || action.payload;
      setStoredToken(token);
      const decoded = jwtDecode<JwtPayload>(token);
      state.isAuthenticating = false;
      state.isAuthenticated = true;
      state.token = token;
      state.userName = decoded.email;
      state.statusText = 'Uspješno ste se prijavili.';
    },
    logout(state) {
      removeStoredToken();
      removeStoredAgentKey();
      state.isAuthenticated = false;
      state.token = null;
      state.userName = null;
      state.statusText = 'Uspješno ste se odjavili.';
      state.isConnected = false;
    },
    wsConnected(state) {
      state.isConnected = true;
    },
    wsDisconnected(state) {
      state.isConnected = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isAuthenticating = true;
        state.statusText = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const token = action.payload.token;
        setStoredToken(token);
        const decoded = decodeToken(token);
        state.isAuthenticating = false;
        state.isAuthenticated = true;
        state.token = token;
        state.userName = decoded.email;
        state.statusText = 'Uspješno ste se prijavili.';
      })
      .addCase(loginUser.rejected, (state) => {
        state.isAuthenticating = false;
        state.isAuthenticated = false;
        state.token = null;
        state.userName = null;
        state.statusText = 'Greška prilikom prijave: Neispravan e-mail ili lozinka';
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.registerStatusText = 'Uspješno ste registrirali novog korisnika.';
      })
      .addCase(registerUser.rejected, (state) => {
        state.registerStatusText = 'Greška pri dodavanju korisnika: Korisnik sa tom e-mail adresom već postoji';
      });
  },
});

export const { loginUserSuccess, logout, wsConnected, wsDisconnected } = authSlice.actions;
export default authSlice.reducer;
